import { useCallback } from 'react';
import { SELLER_DEFAULT_TEXT_FONT_FAMILY } from '@designdeck/shared';
import { IText } from 'fabric';
import { useDesignStore } from '@/stores';
import { useCanvasStateManager } from '@/stores/canvasStateManager';

/**
 * Text style options
 */
export interface TextOptions {
  text?: string;
  fontFamily?: string;
  fontSize?: number;
  fill?: string;
  fontWeight?: string | number;
  fontStyle?: '' | 'normal' | 'italic' | 'oblique';
  textAlign?: string;
  underline?: boolean;
  linethrough?: boolean;
}

/**
 * Available fonts
 */
export const AVAILABLE_FONTS = [
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Georgia',
  'Verdana',
  'Courier New',
  'Impact',
  'Comic Sans MS',
] as const;

/**
 * Hook for text operations on the canvas
 */
export function useTextOperations() {
  const { canvas, selectedObjects, saveToHistory } = useDesignStore();

  /**
   * Add new text to the canvas
   */
  const addText = useCallback(
    (options: TextOptions = {}): IText | null => {
      if (!canvas) return null;

      // The middle of the print area, not of the mockup: on a hoodie the
      // area sits high on the chest, and text created at the canvas centre
      // landed on its lower edge, half clipped.
      const area = useCanvasStateManager.getState().activePrintContext;
      const centre =
        area && area.widthPx > 0 && area.heightPx > 0
          ? {
              left: area.offsetX + area.widthPx / 2,
              top: area.offsetY + area.heightPx / 2,
            }
          : { left: canvas.width! / 2, top: canvas.height! / 2 };

      const text = new IText(options.text || 'Your text here', {
        left: centre.left,
        top: centre.top,
        originX: 'center',
        originY: 'center',
        fontFamily: options.fontFamily || SELLER_DEFAULT_TEXT_FONT_FAMILY,
        fontSize: options.fontSize || 32,
        fill: options.fill || '#000000',
        fontWeight: options.fontWeight || 'normal',
        fontStyle: options.fontStyle || 'normal',
        textAlign: options.textAlign || 'left',
        underline: options.underline || false,
        linethrough: options.linethrough || false,
      });

      canvas.add(text);
      canvas.setActiveObject(text);
      canvas.requestRenderAll();
      saveToHistory('addText');

      return text;
    },
    [canvas, saveToHistory]
  );

  /**
   * Check if active object is text
   */
  const isTextSelected = useCallback((): boolean => {
    if (selectedObjects.length !== 1) return false;
    const obj = selectedObjects[0];
    return obj?.type === 'i-text' || obj?.type === 'textbox';
  }, [selectedObjects]);

  /**
   * Get the selected text object
   */
  const getSelectedText = useCallback((): IText | null => {
    if (!isTextSelected()) return null;
    return selectedObjects[0] as IText;
  }, [selectedObjects, isTextSelected]);

  /**
   * Update text content
   */
  const updateText = useCallback(
    (newText: string) => {
      const textObj = getSelectedText();
      if (!textObj || !canvas) return;

      textObj.set('text', newText);
      canvas.requestRenderAll();
      saveToHistory('updateText');
    },
    [canvas, getSelectedText, saveToHistory]
  );

  /**
   * Update text style property
   */
  const updateTextStyle = useCallback(
    <K extends keyof TextOptions>(property: K, value: TextOptions[K]) => {
      const textObj = getSelectedText();
      if (!textObj || !canvas) return;

      textObj.set(property as keyof IText, value);
      canvas.requestRenderAll();
      saveToHistory('updateTextStyle');
    },
    [canvas, getSelectedText, saveToHistory]
  );

  /**
   * Toggle bold
   */
  const toggleBold = useCallback(() => {
    const textObj = getSelectedText();
    if (!textObj || !canvas) return;

    const currentWeight = textObj.fontWeight;
    const newWeight = currentWeight === 'bold' ? 'normal' : 'bold';
    textObj.set('fontWeight', newWeight);
    canvas.requestRenderAll();
    saveToHistory('toggleBold');
  }, [canvas, getSelectedText, saveToHistory]);

  /**
   * Toggle italic
   */
  const toggleItalic = useCallback(() => {
    const textObj = getSelectedText();
    if (!textObj || !canvas) return;

    const currentStyle = textObj.fontStyle;
    const newStyle = currentStyle === 'italic' ? 'normal' : 'italic';
    textObj.set('fontStyle', newStyle);
    canvas.requestRenderAll();
    saveToHistory('toggleItalic');
  }, [canvas, getSelectedText, saveToHistory]);

  /**
   * Toggle underline
   */
  const toggleUnderline = useCallback(() => {
    const textObj = getSelectedText();
    if (!textObj || !canvas) return;

    textObj.set('underline', !textObj.underline);
    canvas.requestRenderAll();
    saveToHistory('toggleUnderline');
  }, [canvas, getSelectedText, saveToHistory]);

  /**
   * Set font family
   */
  const setFontFamily = useCallback(
    (fontFamily: string) => {
      updateTextStyle('fontFamily', fontFamily);
    },
    [updateTextStyle]
  );

  /**
   * Set font size
   */
  const setFontSize = useCallback(
    (fontSize: number) => {
      updateTextStyle('fontSize', fontSize);
    },
    [updateTextStyle]
  );

  /**
   * Set text color
   */
  const setTextColor = useCallback(
    (color: string) => {
      updateTextStyle('fill', color);
    },
    [updateTextStyle]
  );

  /**
   * Set text alignment
   */
  const setTextAlign = useCallback(
    (align: 'left' | 'center' | 'right') => {
      updateTextStyle('textAlign', align);
    },
    [updateTextStyle]
  );

  return {
    addText,
    isTextSelected,
    getSelectedText,
    updateText,
    updateTextStyle,
    toggleBold,
    toggleItalic,
    toggleUnderline,
    setFontFamily,
    setFontSize,
    setTextColor,
    setTextAlign,
  };
}
